import { createSelector } from 'reselect';
import { getFilters } from '../../webcommon/header/filterheader/selectors';
import fp from 'lodash/fp';
import mappings from './mappings';

export const combinedColumnsSelector = createSelector(
    state => state.impactAnalysis.pricingParameters.columns,
    columns => columns.map(column => ({
        ...column,
        ...mappings.find(col => col.id === column.id)
    }))
);

export const getTrades = state => (
    state.impactAnalysis.tradePricedToday.trades
);

export const getRows = state => (
    state.impactAnalysis.pricingParameters.pricingParametersRows
);

export const getTemplatesNamesLoaded = state => (
    state.impactAnalysis.pricingParameters.templateNamesLoaded
);

export const getFxRates = state => (
    state.impactAnalysis.pricingParameters.fxRates
);

export const getLoading = state => (
    state.impactAnalysis.pricingParameters.loading
);

export const getTemplateSaved = state => (
    state.impactAnalysis.pricingParameters.templateSaved
);

export const getImModelPricingParameters = state => (
    state.impactAnalysis.pricingParameters.imModel
);

export const getSelectedViewImModel = state => (
    state.impactAnalysis.pricingParameters.pricingImModelView
);

export const getDateFrom = (state) => {
    const filterState = getFilters(state, 'ImpactAnalysis');
    if (filterState) {
        const filter = filterState.find(obj => obj.definition.id === 'runDate');
        if (filter && filter.value) {
            return filter.value.from;
        }
    }
    return null;
};

export const getRowMappings = state => (
    state.impactAnalysis.pricingParameters.rowMappings
);

export const getTemplates = state => (
    state.impactAnalysis.pricingParameters.templates
);
export const getTemplateInProcessOfSaving = state => (
    state.impactAnalysis.pricingParameters.templateInProcessOfSaving
);
export const getActiveCsa = (state) => {
    const filterState = getFilters(state, 'ImpactAnalysis');
    if (filterState) {
        const filter = filterState.find(obj => obj.definition.id === 'csa');
        if (filter) {
            if (filter.value.length > 0) {
                return filter.value[0];
            }
        }
        return '';
    }
    return '';
};

const getOptionsForField = (row, field, rowMappings) => {
    if (rowMappings === undefined || rowMappings.crifModelMetaData === undefined) {
        return [];
    }

    if (field === 'products') {
        const { productList } = rowMappings;
        return productList ? Object.keys(productList) : [];
    }

    const availableValues = rowMappings.crifModelMetaData.find(
        option => option.imModel === row.imModel
    );

    const findEditableQualifier = fp.flow(
        fp.get('riskTypeMetaDataList'),
        fp.get(row.riskType),
        fp.get('editableQualifier')
    );

    if (field === 'qualifiers'
    && findEditableQualifier(availableValues)
    && row.riskType !== 'Risk_Commodity') {
        return true;
    }

    if (availableValues !== undefined || field === 'imModel') {
        switch (field) {
            case 'imModel': {
                const { productList } = rowMappings;
                const activeProdClass = row.prodClass;
                const options = fp.get(activeProdClass, productList);
                return options || [];
            }
            case 'riskType':
                return availableValues[field];
            case 'qualifiers':
                return availableValues.riskTypeMetaDataList[row.riskType] !== undefined
                    ? availableValues.riskTypeMetaDataList[row.riskType][field].map(val => val.name)
                    : [];
            default:
                return availableValues.riskTypeMetaDataList[row.riskType] !== undefined
                    ? availableValues.riskTypeMetaDataList[row.riskType][field] : [];
        }
    }
    return [];
};

const getEditableQualifier = (row, rowMappings) => {
    if (rowMappings.crifModelMetaData === undefined) {
        return false;
    }
    const availableValues = rowMappings.crifModelMetaData
        .find(option => option.imModel === row.imModel);

    if (availableValues) {
        try {
            const { editableQualifier } = availableValues.riskTypeMetaDataList[row.riskType];
            return editableQualifier;
        } catch (error) {
            return false;
        }
    }
    return false;
};

const keyLookup = (key) => {
    const mappings = {
        qualifier: 'qualifiers',
        labelOne: 'label1s',
        labelTwo: 'label2s',
        bucket: 'bucket2s',
        prodClass: 'products'
    };
    return mappings[key] || key;
};

const isError = (row, key, activeValue, rowMappings) => {
    if (row.readOnly) {
        return false;
    }
    if (key === 'qualifier' && getEditableQualifier(row, rowMappings)) {
        return false;
    }
    const availableOptions = getOptionsForField(row, keyLookup(key), rowMappings);
    if (key === 'qualifier' || key === 'labelOne' || key === 'labelTwo'
    || key === 'imModel' || key === 'prodClass' || key === 'riskType') {
        if (!availableOptions.includes(activeValue) && activeValue) {
            return true;
        }
    }
    if (key === 'bucket') {
        if (isError(row, 'qualifier', row.qualifier, rowMappings)) {
            return true;
        }
    }
    if (key === 'amount' || key === 'amountUsd') {
        // eslint-disable-next-line no-restricted-globals
        return isNaN(activeValue) && activeValue !== '';
    }
    return false;
};

const mapRow = (row, rowMappings) => Object.keys(row).reduce((reduced, key) => ({
    ...reduced,
    [key]: {
        error: isError(row, key, row[key], rowMappings),
        value: row[key],
        availableOptions: getOptionsForField(row, keyLookup(key), rowMappings)
    }
}), {});

export const getRowsAndErrors = (state) => {
    const { rowMappings } = state.impactAnalysis.pricingParameters;
    return state.impactAnalysis.pricingParameters.pricingParametersRows.map(
        row => mapRow(row, rowMappings)
    );
};
